import {
  Box,
  CircularProgress,
  Grid,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import ArrowRightAltRoundedIcon from '@mui/icons-material/ArrowRightAltRounded';
import { InvoiceStatus, SubscriptionInvoices } from '@prisma/client';
import { LoadingButton } from '@mui/lab';
import { format } from 'date-fns';
import { toast } from 'react-toastify';
import Section from './Section';
import {
  getInvoiceStatusDescription,
  getInvoiceStatusText,
} from '../../helpers/subscriptions';
import { formatDollarAmount } from '../../utils/global/helper';
import useInvoiceHoistedUrl from '../../hooks/subscriptions/useInvoiceHoistedUrl';
import useGetSubscriptionInvoices from '../../hooks/subscriptions/useGetSubscriptionInvoices';
import useGetSubscriptionFromStripe from '../../hooks/subscriptions/useGetSubscriptionFromStripe';

interface InvoiceRowProps {
  invoice: SubscriptionInvoices;
  isPending: boolean;
  onLaunchInvoice: (invoiceId: string) => void | Promise<void>;
}

function InvoiceRow({ invoice, isPending, onLaunchInvoice }: InvoiceRowProps) {
  const theme = useTheme();
  const stripeQuery = useGetSubscriptionFromStripe();
  const { isPaymentFailed } = stripeQuery.state || {};

  const isMissingPayment =
    isPaymentFailed && invoice.status === InvoiceStatus.OPEN;
  const actionText = isMissingPayment ? 'Pay Now' : 'View Invoice';
  const actionColor = isMissingPayment ? 'error' : 'secondary';

  return (
    <Grid
      container
      gap={2}
      overflow="auto"
      alignItems="center"
      wrap="nowrap"
      flexWrap="nowrap"
      sx={{}}>
      <Grid item xs={3}>
        <Typography variant="body1" color={theme.palette.medGrey.main}>
          {format(
            new Date(invoice.chargedAt || invoice.createdAt),
            'MMMM d, y',
          )}
        </Typography>
      </Grid>
      <Grid item xs={3}>
        <Box>
          <Tooltip
            title={getInvoiceStatusDescription(invoice.status)}
            arrow
            placement="top">
            <Typography
              whiteSpace="nowrap"
              variant="h5"
              sx={{ width: 'fit-content' }}>
              {getInvoiceStatusText(invoice.status)}
            </Typography>
          </Tooltip>
        </Box>
      </Grid>
      <Grid item xs={3} flexShrink={0}>
        <Typography
          whiteSpace="nowrap"
          variant="body1"
          color={theme.palette.medGrey.main}>
          {formatDollarAmount(invoice.total / 100)}
        </Typography>
      </Grid>
      <Grid
        item
        xs={3}
        display="flex"
        alignItems="center"
        justifyContent="flex-end">
        {invoice.status !== InvoiceStatus.DRAFT && (
          <LoadingButton
            loading={isPending}
            onClick={() => onLaunchInvoice(invoice.id)}
            disabled={isPending}
            aria-disabled={isPending}
            variant="text"
            color={actionColor}
            endIcon={<ArrowRightAltRoundedIcon />}
            sx={{
              fontWeight: '500',
              fontSize: '18px',
              whiteSpace: 'nowrap',
            }}>
            {actionText}
          </LoadingButton>
        )}
      </Grid>
    </Grid>
  );
}

function BillingHistorySection() {
  const theme = useTheme();

  const { data: invoices = [], isLoading } = useGetSubscriptionInvoices();

  const onError = () => {
    toast.error('Failed to view invoice.');
  };

  const { launchInvoice, checkIsInvoicePending } = useInvoiceHoistedUrl({
    onError,
  });

  const onLaunchInvoice = async (invoiceId: string) =>
    launchInvoice.mutate(invoiceId);

  return (
    <Section title="Billing History">
      <Box display="flex" flexDirection="column" gap={1} width="100%">
        {isLoading && (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            paddingTop={1}>
            <CircularProgress color="primary" />
          </Box>
        )}
        {!invoices?.length && !isLoading && (
          <Typography variant="body1" color={theme.palette.medGrey.main}>
            No invoices yet
          </Typography>
        )}
        {!!invoices?.length && (
          <Stack gap={4} width="100%" overflow="auto">
            {invoices?.map(invoice => {
              const isPending = checkIsInvoicePending(invoice.id);
              return (
                <InvoiceRow
                  key={invoice.id}
                  invoice={invoice}
                  isPending={isPending}
                  onLaunchInvoice={onLaunchInvoice}
                />
              );
            })}
          </Stack>
        )}
      </Box>
    </Section>
  );
}

export default BillingHistorySection;
